import { ReactNode } from 'react';
import { AssetCell } from '../../../common/components/AssetCell/AssetCell';
import { TargetAssetDivider } from '../../TradeAssets/TargetAssetDivider';
import { useFiatWallet } from './FiatWalletContext.use-client';
import { FiatWalletSkeleton } from './FiatWalletSkeleton';
import { getAppConfig } from '@bts-web/core-app-config';
import {
  OpenDrawerFunction,
  useModalDrawerContext,
} from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { BankAccountDetailsDrawer } from './BankAccountDetailsDrawer';
import { useIsBelowScreenWidth } from '../../../layouts/utils/useIsBelowScreenWidth';
import {
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import {
  getTradeAssetSelectorTranslations,
  TradeAssetSelectorTranslations,
} from '../../ChangeCurrencyOrSwap/changeCurrencyUtils';

interface FiatWalletControllerProps {
  fiatIcon: ReactNode;
  amountInput: string;
  locale: string;
  currency: string;
}

const CashCell = ({
  fiatIcon,
  walletAmount,
  currency,
  locale,
  translations,
}: {
  fiatIcon: ReactNode;
  walletAmount: string;
  locale: string;
  currency: string;
  translations: TradeAssetSelectorTranslations;
}) => {
  const valueFormatted = intlFiatValueFormatting(walletAmount, {
    locale,
    currency,
    fractionDigits: MaximumFractionDigits.FIAT,
  });

  return (
    <AssetCell
      dataTestId="buy-source-fiat-wallet"
      icon={fiatIcon}
      nameDescription={translations.payWith}
      name={translations.cash}
      value={valueFormatted}
      valueDescription={translations.available}
    />
  );
};

const TransferCell = ({
  accountNumber,
  bankAccountHolder,
  bankName,
  appLogo,
  openDrawer,
  translations,
}: {
  bankAccountHolder: string;
  accountNumber: string;
  bankName: string;
  appLogo: ReactNode;
  openDrawer: OpenDrawerFunction;
  translations: TradeAssetSelectorTranslations;
}) => {
  const isMobile = useIsBelowScreenWidth(1024);

  const handleTransferDetails = () => {
    openDrawer(BankAccountDetailsDrawer, {
      accountNumber,
      bankAccountHolder,
      bankName,
      position: isMobile ? 'bottom' : 'right',
    });
  };

  return (
    <AssetCell
      dataTestId="buy-source-fiat-wallet"
      icon={appLogo}
      nameDescription={translations.paymentMethod}
      name={translations.onlineTransfer}
      onClick={handleTransferDetails}
    />
  );
};

export const FiatWalletController = ({
  fiatIcon,
  amountInput,
  currency,
  locale,
}: FiatWalletControllerProps) => {
  const { isLoading, hasError, data } = useFiatWallet();

  const { openDrawer } = useModalDrawerContext();

  const { t } = useClientTranslation();

  const { bankLogo: BankLogo } = getAppConfig();

  const translations = getTradeAssetSelectorTranslations(t as TFunction);

  const availableAmount = Number(data?.walletAmount || 0);

  const showCashOption =
    availableAmount > 0 &&
    availableAmount >= Number(amountInput.replace(',', '.'));

  if (isLoading) {
    return (
      <>
        <FiatWalletSkeleton />
        <TargetAssetDivider />
      </>
    );
  }

  if (hasError) {
    return null;
  }

  return (
    <>
      {showCashOption ? (
        <CashCell
          fiatIcon={fiatIcon}
          walletAmount={data?.walletAmount || '0'}
          locale={locale}
          currency={currency}
          translations={translations}
        />
      ) : (
        <TransferCell
          appLogo={BankLogo ? <BankLogo size={40} /> : null}
          accountNumber={data?.accountNumber ?? ''}
          bankAccountHolder={data?.bankAccountHolder ?? ''}
          bankName={data?.bankName ?? ''}
          openDrawer={openDrawer}
          translations={translations}
        />
      )}

      <TargetAssetDivider />
    </>
  );
};
